'use client';

import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import {
  ActionType,
  ComponentType,
  identify,
  init,
  logEvent,
  PlatformName,
} from '@cbhq/client-analytics';

const WALLET_CONNECTED_KEY_PREFIX = 'analytics_wallet_connected_';
const APP_OPENED_KEY = 'analytics_app_opened_logged';
const VERIFICATION_COMPLETED_PREFIX = 'verification_completed_';
const VERIFICATION_STARTED_PREFIX = 'verification_started_';

// Cookie name for the device id
const COOKIE_DEVICE_ID = 'coinbase_device_id';

// https://www.wolframalpha.com/input/?i=630720000000+milliseconds
const TWENTY_YEARS_AS_MS = 630_720_000_000;

function getDeviceId(): string {
  let id = Cookies.get(COOKIE_DEVICE_ID);
  if (!id) {
    id = uuidv4();
    Cookies.set(COOKIE_DEVICE_ID, id, { expires: new Date(Date.now() + TWENTY_YEARS_AS_MS) });
  }
  return id;
}

export function initClientAnalytics() {
  init({
    isProd: process.env.NEXT_PUBLIC_ANALYTICS_ENVIRONMENT === 'production',
    platform: PlatformName.web,
    projectName: 'base_verify_mini_app',
    showDebugLogging: process.env.NEXT_PUBLIC_LOAD_ERUDA === 'true',
    version: '0.0.1',
    amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
  });
  identify({ deviceId: getDeviceId() });
}

export function logPageView(path: string) {
  logEvent('page_viewed', {
    action: ActionType.view,
    componentType: ComponentType.page,
    path,
  });
}

export function logWalletConnectedOnce(address: string) {
  if (typeof window === 'undefined') return; // SSR guard

  const normalized = address.toLowerCase();
  const storageKey = `${WALLET_CONNECTED_KEY_PREFIX}${normalized}`;

  try {
    const alreadyLogged = sessionStorage.getItem(storageKey);
    if (alreadyLogged) return;

    logEvent('wallet_connected', {
      action: ActionType.submit,
      componentType: ComponentType.button,
      walletAddress: normalized,
    });

    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Log best-effort if sessionStorage is unavailable
    logEvent('wallet_connected', {
      action: ActionType.submit,
      componentType: ComponentType.button,
      walletAddress: normalized,
    });
  }
}

export function logAppOpenedOnce() {
  if (typeof window === 'undefined') return; // SSR guard

  const sp = new URLSearchParams(window.location.search);
  const redirectUri = (sp.get('redirect_uri') ?? '').trim();
  const appOpened = `app_opened_${redirectUri ? 'external' : 'tba'}`;

  try {
    const alreadyLogged = sessionStorage.getItem(APP_OPENED_KEY);
    if (alreadyLogged) return;

    logEvent(appOpened, {
      action: ActionType.view,
      componentType: ComponentType.page,
      redirectUri,
    });

    sessionStorage.setItem(APP_OPENED_KEY, '1');
  } catch {
    // Log best-effort if sessionStorage is unavailable
    logEvent(appOpened, {
      action: ActionType.view,
      componentType: ComponentType.page,
      redirectUri,
    });
  }
}

export function logVerificationStarted(provider: string) {
  if (typeof window === 'undefined') return; // SSR guard

  logEvent(`verification_started_${provider}`, {
    action: ActionType.submit,
    componentType: ComponentType.button,
  });

  const storageKey = `${VERIFICATION_COMPLETED_PREFIX}${provider.toLowerCase()}`;
  try {
    sessionStorage.removeItem(storageKey);
    sessionStorage.setItem(`${VERIFICATION_STARTED_PREFIX}${provider.toLowerCase()}`, '1');
  } catch {
    // Ignore errors from sessionStorage
  }
}

export function logVerificationDeleted(provider: string) {
  logEvent(`verification_deleted_${provider}`, {
    action: ActionType.submit,
    componentType: ComponentType.button,
  });
}

export function logVerificationCompleted(provider: string, originRedirectUri: string) {
  if (typeof window === 'undefined') return; // SSR guard

  const storageKey = `${VERIFICATION_COMPLETED_PREFIX}${provider.toLowerCase()}`;
  try {
    const alreadyLogged = sessionStorage.getItem(storageKey);
    if (alreadyLogged) return;

    logEvent(`verification_completed_${provider}`, {
      action: ActionType.submit,
      componentType: ComponentType.page,
      originRedirectUri,
    });

    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Log best-effort if sessionStorage is unavailable
    logEvent(`verification_completed_${provider}`, {
      action: ActionType.submit,
      componentType: ComponentType.page,
      originRedirectUri,
    });
  }
}

/**
 * Returns true if the 'verification_started' key for the provider is set and clears it.
 */
export function clearVerificationStartedKey(provider: string): boolean {
  if (typeof window === 'undefined') return false; // SSR guard
  const key = `${VERIFICATION_STARTED_PREFIX}${provider.toLowerCase()}`;
  try {
    const val = sessionStorage.getItem(key);
    if (val) {
      sessionStorage.removeItem(key);
      return true;
    }
  } catch {
    // Ignore errors from sessionStorage
  }
  return false;
}
